import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, PriceInput, Select } from '@artosera/ui';
import {
  CATEGORIES_DEPENSE,
  useDepenses,
  useCharges,
  useSupprimerDepense,
  useCreerCharge,
  useModifierCharge,
  messageTresorerie,
  type Depense,
  type Charge,
  type CorpsCharge,
  type FiltresDepenses,
} from '../../api/tresorerie.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { TresorerieCadre, Montant, DateJour, aujourdhui, EtatRequete } from './commun.js';
import { DepenseFormulaire } from './DepenseFormulaire.js';
import styles from './Tresorerie.module.css';
import { Verrouillable } from '../module/LectureSeule.js';
function FormulaireCharge({ charge, fermer }: { charge?: Charge; fermer: () => void }) {
  const { galerieSlug } = useShellContext();
  const { t, i18n } = useTranslation('regie');
  const creer = useCreerCharge(galerieSlug);
  const modifier = useModifierCharge(galerieSlug);
  const [corps, setCorps] = useState<CorpsCharge>(
    charge
      ? {
          libelle: charge.libelle,
          categorie: charge.categorie,
          montantCentimes: charge.montantCentimes,
          periodicite: charge.periodicite,
          premierMois: charge.premierMois,
          dernierMois: charge.dernierMois,
          jourDuMois: charge.jourDuMois,
          active: charge.active,
        }
      : {
          libelle: '',
          categorie: 'autre',
          montantCentimes: 0,
          periodicite: 'mensuelle',
          premierMois: aujourdhui().slice(0, 7),
          jourDuMois: 1,
          active: true,
        },
  );
  const [erreur, setErreur] = useState('');
  const label = (c: string) => t(`tresorerie.${c}`);
  async function soumettre() {
    setErreur('');
    try {
      if (charge) await modifier.mutateAsync({ id: charge.id, corps });
      else await creer.mutateAsync(corps);
      fermer();
    } catch (e) {
      setErreur(messageTresorerie(e));
    }
  }
  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        void soumettre();
      }}
    >
      <Field htmlFor="charge-libelle" label={label('libelle')}>
        <Input
          id="charge-libelle"
          value={corps.libelle}
          required
          onChange={(e) => setCorps({ ...corps, libelle: e.target.value })}
        />
      </Field>
      <Field htmlFor="charge-categorie" label={label('categorie')}>
        <Select
          id="charge-categorie"
          value={corps.categorie}
          onValueChange={(v) => setCorps({ ...corps, categorie: v as CorpsCharge['categorie'] })}
          options={CATEGORIES_DEPENSE.map((v) => ({ value: v, label: label(`categories.${v}`) }))}
        />
      </Field>
      <Field htmlFor="charge-montant" label={label('montant')}>
        <PriceInput
          id="charge-montant"
          cents={corps.montantCentimes}
          onCentsChange={(v) => setCorps({ ...corps, montantCentimes: v })}
          lang={i18n.language === 'en' ? 'en' : 'fr'}
        />
      </Field>
      <Field htmlFor="charge-periodicite" label={label('periodicite')}>
        <Select
          id="charge-periodicite"
          value={corps.periodicite}
          onValueChange={(v) =>
            setCorps({ ...corps, periodicite: v as CorpsCharge['periodicite'] })
          }
          options={['mensuelle', 'trimestrielle', 'annuelle'].map((v) => ({
            value: v,
            label: label(`periodicites.${v}`),
          }))}
        />
      </Field>
      <Field htmlFor="charge-premier" label={label('premierMois')}>
        <Input
          id="charge-premier"
          type="month"
          required
          value={corps.premierMois}
          onChange={(e) => setCorps({ ...corps, premierMois: e.target.value })}
        />
      </Field>
      <Field htmlFor="charge-dernier" label={label('dernierMois')}>
        <Input
          id="charge-dernier"
          type="month"
          min={corps.premierMois}
          value={corps.dernierMois ?? ''}
          onChange={(e) => setCorps({ ...corps, dernierMois: e.target.value || undefined })}
        />
      </Field>
      <Field htmlFor="charge-jour" label={label('jourDuMois')}>
        <Input
          id="charge-jour"
          type="number"
          min={1}
          max={28}
          required
          value={corps.jourDuMois}
          onChange={(e) => setCorps({ ...corps, jourDuMois: Number(e.target.value) })}
        />
      </Field>
      <label>
        <input
          type="checkbox"
          checked={corps.active}
          onChange={(e) => setCorps({ ...corps, active: e.target.checked })}
        />{' '}
        {label('active')}
      </label>
      {erreur && <p role="alert">{erreur}</p>}
      <Button
        type="submit"
        loading={creer.isPending || modifier.isPending}
        disabled={corps.montantCentimes <= 0}
      >
        {label('enregistrerCharge')}
      </Button>
    </form>
  );
}
function Contenu() {
  const { galerieSlug, galerie } = useShellContext();
  const { t } = useTranslation('regie');
  const [filtres, setFiltres] = useState<FiltresDepenses>({});
  const depenses = useDepenses(galerieSlug, filtres);
  const charges = useCharges(galerieSlug);
  const supprimer = useSupprimerDepense(galerieSlug);
  const [depense, setDepense] = useState<Depense | 'nouvelle'>();
  const [charge, setCharge] = useState<Charge | 'nouvelle'>();
  const [suppression, setSuppression] = useState<Depense>();
  const [erreur, setErreur] = useState('');
  const label = (c: string) => t(`tresorerie.${c}`);
  const peutEcrire = galerieALaction(galerie, 'treasury:write');
  return (
    <>
      <div className={styles.actions}>
        <Field htmlFor="depenses-etat" label={label('etat')}>
          <select
            id="depenses-etat"
            value={filtres.etat ?? ''}
            onChange={(e) =>
              setFiltres({
                ...filtres,
                etat: (e.target.value || undefined) as FiltresDepenses['etat'],
              })
            }
          >
            {['', 'a_payer', 'payee'].map((v) => (
              <option key={v} value={v}>
                {label(v || 'tous')}
              </option>
            ))}
          </select>
        </Field>
        <Field htmlFor="depenses-categorie" label={label('categorie')}>
          <select
            id="depenses-categorie"
            value={filtres.categorie ?? ''}
            onChange={(e) =>
              setFiltres({
                ...filtres,
                categorie: (e.target.value || undefined) as FiltresDepenses['categorie'],
              })
            }
          >
            <option value="">{label('tous')}</option>
            {CATEGORIES_DEPENSE.map((v) => (
              <option key={v} value={v}>
                {label(`categories.${v}`)}
              </option>
            ))}
          </select>
        </Field>
        {(['du', 'au'] as const).map((c) => (
          <Field key={c} htmlFor={`depenses-${c}`} label={label(c)}>
            <Input
              id={`depenses-${c}`}
              type="date"
              value={filtres[c] ?? ''}
              onChange={(e) => setFiltres({ ...filtres, [c]: e.target.value || undefined })}
            />
          </Field>
        ))}
        <Verrouillable action="treasury:write" ouvert={peutEcrire}>
          <Button onClick={() => setDepense('nouvelle')}>{label('nouvelleDepense')}</Button>
        </Verrouillable>
      </div>
      <EtatRequete requete={depenses} />
      {erreur && <p role="alert">{erreur}</p>}
      {depenses.data && (
        <div className={styles.tableau}>
          <table>
            <caption>{label('depenses')}</caption>
            <thead>
              <tr>
                {['libelle', 'fournisseur', 'categorie', 'date', 'echeance', 'montant', 'etat'].map(
                  (c) => (
                    <th scope="col" key={c}>
                      {label(c)}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {depenses.data.depenses.map((d) => (
                <tr key={d.id}>
                  <td>{d.libelle}</td>
                  <td>{d.fournisseurNom}</td>
                  <td>{label(`categories.${d.categorie}`)}</td>
                  <td>
                    <DateJour date={d.date} />
                  </td>
                  <td>
                    <DateJour date={d.echeanceLe} />
                  </td>
                  <td>
                    <Montant cents={d.montantCentimes} />
                  </td>
                  <td>
                    {label(d.etat)}
                    {peutEcrire && d.etat === 'a_payer' && (
                      <>
                        <Button onClick={() => setDepense(d)}>{label('modifier')}</Button>
                        <Button onClick={() => setSuppression(d)}>{label('supprimer')}</Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <h2>{label('charges')}</h2>
      <Verrouillable action="treasury:write" ouvert={peutEcrire}>
        <Button onClick={() => setCharge('nouvelle')}>{label('nouvelleCharge')}</Button>
      </Verrouillable>
      <EtatRequete requete={charges} />
      <ul>
        {charges.data?.charges.map((c) => (
          <li key={c.id}>
            {c.libelle} · <Montant cents={c.montantCentimes} /> ·{' '}
            {label(`periodicites.${c.periodicite}`)} · {label(c.active ? 'active' : 'inactive')}
            <Verrouillable action="treasury:write" ouvert={peutEcrire}>
              <Button onClick={() => setCharge(c)}>{label('modifier')}</Button>
            </Verrouillable>
          </li>
        ))}
      </ul>
      {depense && peutEcrire && (
        <Dialog
          open
          onOpenChange={(v) => {
            if (!v) setDepense(undefined);
          }}
          title={label('depenses')}
          closeLabel={label('fermer')}
        >
          <DepenseFormulaire
            depense={depense === 'nouvelle' ? undefined : depense}
            onClose={() => setDepense(undefined)}
          />
        </Dialog>
      )}
      {charge && peutEcrire && (
        <Dialog
          open
          onOpenChange={(v) => {
            if (!v) setCharge(undefined);
          }}
          title={label('charges')}
          closeLabel={label('fermer')}
        >
          <FormulaireCharge
            charge={charge === 'nouvelle' ? undefined : charge}
            fermer={() => setCharge(undefined)}
          />
        </Dialog>
      )}
      {suppression && peutEcrire && (
        <Dialog
          open
          onOpenChange={(v) => {
            if (!v) setSuppression(undefined);
          }}
          title={label('supprimerDepense')}
          closeLabel={label('fermer')}
        >
          <p>{suppression.libelle}</p>
          <Button
            loading={supprimer.isPending}
            onClick={() => {
              void supprimer
                .mutateAsync({ id: suppression.id, versionAttendue: suppression.version })
                .then(() => setSuppression(undefined))
                .catch((e: unknown) => setErreur(messageTresorerie(e)));
            }}
          >
            {label('confirmer')}
          </Button>
        </Dialog>
      )}
    </>
  );
}
export function DepensesPage() {
  return (
    <TresorerieCadre titre="depenses">
      <Contenu />
    </TresorerieCadre>
  );
}
